import { useQuery } from '@tanstack/react-query'
import { getDocuments } from '../../services/api'
import { Puzzle, AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react'
import type { Document } from '../../types'

interface ReferenceComponentsListProps {
  projectId: string
}

function statusBadge(status?: string) {
  switch (status) {
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Completed
        </span>
      )
    case 'processing':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          <Loader2 className="w-3 h-3 animate-spin" /> Processing
        </span>
      )
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
          <AlertCircle className="w-3 h-3" /> Failed
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          <Clock className="w-3 h-3" /> Pending
        </span>
      )
  }
}

export default function ReferenceComponentsList({ projectId }: ReferenceComponentsListProps) {
  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => getDocuments(projectId),
    enabled: !!projectId,
  })

  const referenceDocuments = (documents ?? []).filter(
    (doc: Document) => doc.component_role === 'reference'
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading reference components...
      </div>
    )
  }

  if (referenceDocuments.length === 0) {
    return (
      <div className="text-center py-12">
        <Puzzle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500 font-medium">No reference components</p>
        <p className="text-xs text-gray-500 mt-1">
          Documents classified as reference components will appear here.
          They are excluded from synthesis and novelty analysis.
        </p>
      </div>
    )
  }

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        Reference components are excluded from AI synthesis and novelty analysis.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sky-200/50 text-left">
              <th className="pb-2 pr-4 font-medium text-gray-600">Part Name</th>
              <th className="pb-2 pr-4 font-medium text-gray-600">Standard Designation</th>
              <th className="pb-2 pr-4 font-medium text-gray-600">Domain</th>
              <th className="pb-2 font-medium text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sky-100/50">
            {referenceDocuments.map((doc: Document) => (
              <tr key={doc.document_id} className="hover:bg-sky-50/30">
                <td className="py-2.5 pr-4 text-gray-900 font-medium">{doc.title}</td>
                <td className="py-2.5 pr-4 text-gray-600">{doc.detected_standard || '-'}</td>
                <td className="py-2.5 pr-4 text-gray-600">{doc.domain || '-'}</td>
                <td className="py-2.5">{statusBadge(doc.embedding_status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
